import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Volume2, Palette, Shield, Download, 
  Trash2, Moon, Sun, Globe, User, Bell, Info,
  CheckCircle, Database, Share2, HardDrive, Play, Pause
} from 'lucide-react';
import { getVersionString } from '../version';

interface UserSettings {
  voice: string;
  theme: 'light' | 'dark' | 'auto';
  language: string;
  notifications: boolean;
  autoPlay: boolean;
  speechRate: number;
}

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<UserSettings>({
    voice: 'rachel',
    theme: 'dark',
    language: 'en',
    notifications: false,
    autoPlay: true,
    speechRate: 1.0
  });
  const [storageUsed, setStorageUsed] = useState(0);
  const [recordingCount, setRecordingCount] = useState(0);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [loadingVoice, setLoadingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const voices = [
    { id: 'rachel', name: 'Rachel', desc: 'Calm & supportive' },
    { id: 'bella', name: 'Bella', desc: 'Soft & gentle' },
    { id: 'antoni', name: 'Antoni', desc: 'Warm & friendly' },
    { id: 'josh', name: 'Josh', desc: 'Energetic & upbeat' },
    { id: 'arnold', name: 'Arnold', desc: 'Deep & authoritative' },
  ];

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }

    // Calculate storage usage
    calculateStorage();

    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      speechSynthesis.cancel();
    };
  }, []);

  const calculateStorage = () => {
    const recordings = JSON.parse(localStorage.getItem('recordings') || '[]');
    setRecordingCount(recordings.length);
    
    // Estimate storage size
    const storageString = JSON.stringify(localStorage);
    const sizeInBytes = new Blob([storageString]).size;
    setStorageUsed(sizeInBytes);
  };

  const saveSetting = (key: keyof UserSettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('userSettings', JSON.stringify(newSettings));
    
    // Apply theme immediately if changed
    if (key === 'theme') {
      applyTheme(value);
    }
  };

  const applyTheme = (theme: string) => {
    if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  };

  const exportData = () => {
    const data = {
      exportDate: new Date().toISOString(),
      version: getVersionString(),
      settings: settings,
      recordings: JSON.parse(localStorage.getItem('recordings') || '[]'),
      analytics: JSON.parse(localStorage.getItem('analytics') || '{}')
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selfi-backup-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAllData = () => {
    if (showClearConfirm) {
      localStorage.clear();
      navigate('/');
      window.location.reload();
    } else {
      setShowClearConfirm(true);
      setTimeout(() => setShowClearConfirm(false), 3000);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const previewVoice = async (voiceId: string) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    if (playingVoice === voiceId) {
      setPlayingVoice(null);
      return;
    }

    setLoadingVoice(voiceId);
    setPlayingVoice(voiceId);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const sampleText = `Hi there! I'm ${voices.find(v => v.id === voiceId)?.name}. I'll be your motivational coach, here to support and encourage you on your journey. Let's work together to achieve your goals!`;
      
      const response = await fetch(`${apiUrl}/api/audio/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: sampleText, 
          voice: voiceId 
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          const audio = new Audio(data.audio_url);
          audioRef.current = audio;
          
          audio.onended = () => {
            setPlayingVoice(null);
            audioRef.current = null;
          };
          
          audio.onerror = () => {
            setPlayingVoice(null);
            audioRef.current = null;
          };
          
          await audio.play();
        }
      } else {
        // Fallback to browser TTS
        const utterance = new SpeechSynthesisUtterance(sampleText);
        utterance.rate = settings.speechRate;
        utterance.onend = () => setPlayingVoice(null);
        speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Error previewing voice:', error);
      // Use browser TTS as fallback
      const sampleText = `Hi, I'm ${voices.find(v => v.id === voiceId)?.name}`;
      const utterance = new SpeechSynthesisUtterance(sampleText);
      utterance.rate = settings.speechRate;
      utterance.onend = () => setPlayingVoice(null);
      speechSynthesis.speak(utterance);
    } finally {
      setLoadingVoice(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-slate-900 to-purple-900 text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold">Settings</h1>
        </div>
      </div>

      <div className="p-4 space-y-6 pb-20">
        {/* Voice Settings */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Volume2 className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-semibold">Voice & Audio</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">AI Coach Voice</label>
              <div className="space-y-2">
                {voices.map(voice => (
                  <div
                    key={voice.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                      settings.voice === voice.id 
                        ? 'bg-purple-500/20 border-purple-500' 
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                    onClick={() => saveSetting('voice', voice.id)}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{voice.name}</div>
                      <div className="text-xs text-gray-400">{voice.desc}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        previewVoice(voice.id);
                      }}
                      disabled={loadingVoice === voice.id}
                      className={`p-2 rounded-lg transition-colors ${
                        playingVoice === voice.id 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      {loadingVoice === voice.id ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : playingVoice === voice.id ? (
                        <Pause className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm">Auto-play responses</span>
              <button
                onClick={() => saveSetting('autoPlay', !settings.autoPlay)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.autoPlay ? 'bg-purple-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                  settings.autoPlay ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                Speech Rate: {settings.speechRate}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={settings.speechRate}
                onChange={(e) => saveSetting('speechRate', parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Palette className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-semibold">Appearance</h2>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            {(['dark', 'light', 'auto'] as const).map(theme => (
              <button
                key={theme}
                onClick={() => saveSetting('theme', theme)}
                className={`p-3 rounded-lg border transition-all ${
                  settings.theme === theme 
                    ? 'bg-purple-500/20 border-purple-500' 
                    : 'bg-white/5 border-white/10'
                }`}
              >
                {theme === 'dark' && <Moon className="w-5 h-5 mx-auto mb-1" />}
                {theme === 'light' && <Sun className="w-5 h-5 mx-auto mb-1" />}
                {theme === 'auto' && <Globe className="w-5 h-5 mx-auto mb-1" />}
                <span className="text-xs capitalize">{theme}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Privacy & Data */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-green-400" />
            <h2 className="text-lg font-semibold">Privacy & Data</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
              <HardDrive className="w-5 h-5 text-green-400" />
              <div className="flex-1">
                <p className="text-sm font-medium">Local Storage Only</p>
                <p className="text-xs text-gray-400">Your data never leaves this device</p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>

            <div className="flex items-center justify-between text-sm">
              <span>Storage Used</span>
              <span className="text-purple-400">{formatBytes(storageUsed)}</span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span>Total Recordings</span>
              <span className="text-purple-400">{recordingCount}</span>
            </div>

            <button
              onClick={exportData}
              className="w-full flex items-center justify-center gap-2 py-3 bg-purple-500/20 rounded-lg hover:bg-purple-500/30 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export All Data</span>
            </button>

            <button
              onClick={clearAllData}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg transition-colors ${
                showClearConfirm 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-red-500/20 hover:bg-red-500/30'
              }`}
            >
              <Trash2 className="w-4 h-4" />
              <span>{showClearConfirm ? 'Tap again to confirm' : 'Clear All Data'}</span>
            </button>
          </div>
        </div>

        {/* About */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Info className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-semibold">About</h2>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Version</span>
              <span>{getVersionString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Website</span>
              <a href="https://selfi.fyi" className="text-purple-400">selfi.fyi</a>
            </div>
            <div className="pt-3 border-t border-white/10">
              <p className="text-xs text-gray-400 text-center">
                Selfi - Your inner voice, amplified
              </p>
              <p className="text-xs text-gray-500 text-center mt-1">
                Made with 💜 for personal growth
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};